import { useCallback, useState, type ClipboardEvent } from "react";

import { OpenInNew, Worldwide } from "@similarweb/icons";
import { Button, Modal } from "@similarweb/ui-components";
import { TextField } from "@similarweb/ui-components/forms";

import { translate } from "app/filters/i18nFilter";
import { INTEGRATION_TERMS } from "app/pages/sales-intelligence/pages/management/constants/constants";

import { CUSTOM_MODAL_STYLES, StyledTermsBox, StyledTermsLink, StyledTermsText, StyledTermsTitle } from "./styles";
import {
    DYNAMICS_CONNECT_MODAL_ACCEPT_BUTTON,
    DYNAMICS_CONNECT_MODAL_CANCEL_BUTTON,
    DYNAMICS_CONNECT_MODAL_INSTANCE_URL_ERROR,
    DYNAMICS_CONNECT_MODAL_INSTANCE_URL_HINT,
    DYNAMICS_CONNECT_MODAL_INSTANCE_URL_LABEL,
    DYNAMICS_CONNECT_MODAL_INSTANCE_URL_PLACEHOLDER,
    DYNAMICS_CONNECT_MODAL_TERMS_TEXT,
    DYNAMICS_CONNECT_MODAL_TERMS_TITLE,
    DYNAMICS_CONNECT_MODAL_TITLE,
} from "./translations";
import { isValidDynamicsInstanceUrl, parseDynamicsInstanceUrl } from "./parseDynamicsInstanceUrl";

type DynamicsConnectModalProps = {
    isOpen: boolean;
    onConfirm(environmentUrl: string): void;
    onClose(): void;
};

export const DynamicsConnectModal = ({ isOpen, onConfirm, onClose }: DynamicsConnectModalProps) => {
    const [url, setUrl] = useState("");
    const [hasInteracted, setHasInteracted] = useState(false);

    const trimmedUrl = url.trim();
    const isValid = isValidDynamicsInstanceUrl(trimmedUrl);
    const showError = hasInteracted && trimmedUrl.length > 0 && !isValid;

    const handleChange = useCallback(
        (value: string) => {
            setUrl(value);
            if (!hasInteracted) {
                setHasInteracted(true);
            }
        },
        [hasInteracted],
    );

    const handleBlur = useCallback(() => {
        setHasInteracted(true);
    }, []);

    const handlePaste = useCallback(
        (event: ClipboardEvent<HTMLInputElement>) => {
            const pastedText = event.clipboardData.getData("text");
            const normalizedUrl = parseDynamicsInstanceUrl(pastedText);

            if (normalizedUrl !== pastedText.trim()) {
                event.preventDefault();
                setUrl(normalizedUrl);
                if (!hasInteracted) {
                    setHasInteracted(true);
                }
            }
        },
        [hasInteracted],
    );

    const handleConfirm = useCallback(() => {
        if (isValid) {
            onConfirm(trimmedUrl);
        }
    }, [isValid, trimmedUrl, onConfirm]);

    const handleClose = useCallback(() => {
        setUrl("");
        setHasInteracted(false);
        onClose();
    }, [onClose]);

    return (
        <Modal
            isOpen={isOpen}
            shouldCloseOnOverlayClick
            onRequestClose={handleClose}
            customStyles={CUSTOM_MODAL_STYLES}
            data-automation="dynamics-connect-modal"
        >
            <Modal.CloseButton onClick={handleClose} />
            <Modal.Header>
                <Modal.Title>{translate(DYNAMICS_CONNECT_MODAL_TITLE)}</Modal.Title>
            </Modal.Header>
            <Modal.Content
                gap="24px"
                paddingX="24px"
            >
                <TextField
                    label={translate(DYNAMICS_CONNECT_MODAL_INSTANCE_URL_LABEL)}
                    startAddon={<Worldwide size="xs" />}
                    placeholder={translate(DYNAMICS_CONNECT_MODAL_INSTANCE_URL_PLACEHOLDER)}
                    value={url}
                    onChange={handleChange}
                    onPaste={handlePaste}
                    onBlur={handleBlur}
                    isInvalid={showError}
                    errorMessage={translate(DYNAMICS_CONNECT_MODAL_INSTANCE_URL_ERROR)}
                    description={!showError ? translate(DYNAMICS_CONNECT_MODAL_INSTANCE_URL_HINT) : undefined}
                    data-automation="dynamics-connect-modal-url-input"
                />
                <StyledTermsBox>
                    <StyledTermsTitle>{translate(DYNAMICS_CONNECT_MODAL_TERMS_TITLE)}</StyledTermsTitle>
                    <StyledTermsText>{translate(DYNAMICS_CONNECT_MODAL_TERMS_TEXT)}</StyledTermsText>
                    <StyledTermsLink
                        target="_blank"
                        rel="noreferrer"
                        href={INTEGRATION_TERMS.dynamics}
                    >
                        <OpenInNew size="xs" />
                        {INTEGRATION_TERMS.dynamics}
                    </StyledTermsLink>
                </StyledTermsBox>
            </Modal.Content>
            <Modal.Footer>
                <Button
                    type="flat"
                    onClick={handleClose}
                    dataAutomation="dynamics-connect-modal-cancel"
                >
                    {translate(DYNAMICS_CONNECT_MODAL_CANCEL_BUTTON)}
                </Button>
                <Button
                    type="primary"
                    isDisabled={!isValid}
                    onClick={handleConfirm}
                    dataAutomation="dynamics-connect-modal-accept"
                >
                    {translate(DYNAMICS_CONNECT_MODAL_ACCEPT_BUTTON)}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
